import React, { useEffect, useRef, useState } from "react";
import type { MatchConfig, Player } from "../../shared/types";
import { saveLocalPlayer } from "../../shared/local-players";
import { parseEvent, CONTINUE_KEY } from "../../shared/key-util.js";
import { isInputElementActive } from "../../shared/client-state-functions.js";
import { useIsTouch } from "../../shared/useResponsive";
import { useNavigate } from "react-router-dom";
import { OwnLine, botTag } from "./LobbyRoom";
import { B, Keys, RoomsPage } from "./RoomsUI";
import { configOf, mapValueOfRules, rollShape, type Rules } from "./roomRules";
import { RuleRows } from "./RuleRows";
import { AddPlayer, KindMenu, Panel, RoomLayout, type Kind } from "./RoomParts";
import { genLobbyCode } from "../lobby/lobby";
import { useLandscapePhone } from "../lobby/start/hooks";
import rp from "./roomPage.module.scss";
import { createMapFromValue } from "./maps";
import { loadLocalRules, saveLocalRules } from "./localSetupStore";
import { useMatchSetup, MAX_COUCH_SCHEMES, MAX_PLAYERS } from "./useMatchSetup";
import s from "./rooms.module.scss";

interface Props {
  onStartMatch: (cfg: MatchConfig) => void;
  onBack?: () => void;
}

// A local game: everyone on this keyboard. The players (each with a colour and
// keys), a flavour, the rules as one line with Change, and Start. Builds a core
// MatchConfig and starts it. The roster is the setup hook's (it remembers your
// last one); the rules are the same easy rules a Game Room has.
export default function LocalSetupPage({ onStartMatch, onBack }: Props) {
  const touch = useIsTouch();
  const roster = useMatchSetup();
  const [saved] = useState(loadLocalRules);
  const [rules, setRulesState] = useState<Rules>(saved);
  const navigate = useNavigate();
  const land = useLandscapePhone();

  // Who plays is the roster, so the rules' seat count simply follows it (the
  // map size and the score target follow the seats in turn).
  const count = roster.players.length;
  const live: Rules = { ...rules, players: Math.max(2, count) };
  const setRules = (fn: (r: Rules) => Rules) => setRulesState((r) => fn({ ...r, players: Math.max(2, count) }));
  useEffect(() => saveLocalRules(rules), [rules]);

  // Keep player 1 & 2's name/color/keys in sync with the shared store (so the
  // other setup screens agree on the couch primaries).
  useEffect(() => {
    roster.players.slice(0, 2).forEach((p, i) => saveLocalPlayer(i, { name: p.name, colorId: p.colorId, left: p.left, right: p.right }));
  }, [roster.players]);

  const humans = roster.players.filter((p) => p.type === "human");
  const extras = roster.players.filter((p) => p.type !== "human");
  const canStart = count >= 2;

  const start = () => {
    if (!canStart || touch) return;
    // "Surprise me" draws the map now.
    const r: Rules = live.shape === "surprise" ? { ...live, rolled: rollShape() } : live;
    const c = configOf(r);
    // Each extra player plays at the level they were added with.
    const players: Player[] = roster.players.map((p) => ({ ...p }));
    onStartMatch({ players, map: createMapFromValue(mapValueOfRules(r)), maxScore: c.maxScore, maxRounds: c.maxRounds, disabledPowerUps: c.disabledPowerUps, powerUpSpawnChance: c.powerUpSpawnChance, wormSpeed: c.wormSpeed, respawn: c.respawn || undefined } as MatchConfig);
  };

  // SPACE starts the match (matches the old local setup + the e2e smoke test),
  // unless a text field is focused. Registered once; reads the latest builder.
  const startRef = useRef(start);
  startRef.current = start;
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isInputElementActive()) return;
      if (parseEvent(e) === CONTINUE_KEY) startRef.current();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Phones are one player each: a local game needs a keyboard.
  if (touch) {
    return (
      <RoomsPage title="Local game" sub="Everyone on one keyboard" onBack={onBack}>
        <div className={s.needKeys}>
          <span className={s.needIcon}><Keys /></span>
          <b>Local play needs a keyboard</b>
          {onBack && <B className={s.change} onClick={onBack}>Back</B>}
        </div>
      </RoomsPage>
    );
  }

  // Who can join, from the header: Private or Public turns this game into a
  // Game Room with the same rules.
  const pickKind = (k: Kind) => { if (k !== "local") navigate(`/lobby/${genLobbyCode()}`, { state: { public: k === "public", rules: live } }); };

  const playersCol = (<>
    <Panel className={rp.playersTop}>
      <AddPlayer
        local={{ onAdd: () => roster.addLocalPlayer(MAX_COUCH_SCHEMES), keys: roster.nextKeys.left ? `${roster.nextKeys.left} / ${roster.nextKeys.right}` : null, off: count >= MAX_PLAYERS ? "No more seats" : humans.length >= MAX_COUCH_SCHEMES ? `${MAX_COUCH_SCHEMES} players is the most on one keyboard` : null }}
        bot={{ onAdd: (l) => roster.addBot(l), off: count >= MAX_PLAYERS }}
        full={count >= MAX_PLAYERS ? { text: `${MAX_PLAYERS} players is the most` } : null}
      />
    </Panel>
    <Panel className={rp.seatsCol}>
      <div className={rp.cardHead}>Players ({count})</div>
      <div className={s.seats + " " + s.seatsOne}>
        {humans.map((p, i) => <OwnLine key={p.id} p={p} edit={roster} keys removable={i > 0} noFlag />)}
        {extras.map((p) => <OwnLine key={p.id} p={p} edit={roster} removable tag={botTag(p.skill)} noFlag />)}
      </div>
    </Panel>
  </>);
  const startBtn = <B className={rp.start} onClick={start} disabled={!canStart} data-sound="confirm">{canStart ? "Start game" : "Add a second player"}</B>;

  return (
    <RoomsPage wide title="Local game" sub="Everyone on this keyboard" onBack={onBack} after={<KindMenu kind="local" onPick={pickKind} />}>
      <RoomLayout land={land} players={playersCol} rules={<RuleRows rules={live} setRules={setRules} local />} start={startBtn} />
    </RoomsPage>
  );
}
